import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/contexts/AuthContext';
import {
  FEEDBACK_COOLDOWN_EVENT,
  FEEDBACK_COOLDOWN_MS,
  feedbackStorageKey,
  feedbackUserId,
  isFeedbackOnCooldown,
  markFeedbackSubmitted,
} from './feedbackCooldown';
import {
  FEEDBACK_MESSAGE_MAX,
  FEEDBACK_RATINGS,
  resolveFeedbackSource,
  type FeedbackRatingValue,
} from './feedbackSource';
import { submitProductFeedback } from './submitProductFeedback';
import './FeedbackWidget.css';

const ERROR_MESSAGE = 'Unable to submit your feedback. Please try again.';
const TRUSTPILOT_REVIEW_URL = 'https://www.trustpilot.com/review/hireveda.com';

function TrustpilotNote() {
  return (
    <a
      className="hv-feedback-review-btn"
      href={TRUSTPILOT_REVIEW_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      Consider writing a review on Trustpilot
    </a>
  );
}

type FeedbackWidgetProps = {
  variant?: 'link' | 'nav';
  align?: 'start' | 'end';
  className?: string;
  wrapperClassName?: string;
  style?: React.CSSProperties;
};

function useFeedbackCooldown(userId: string | null) {
  const [onCooldown, setOnCooldown] = useState(() => (userId ? isFeedbackOnCooldown(userId) : false));

  useEffect(() => {
    if (!userId) return undefined;
    const sync = () => setOnCooldown(isFeedbackOnCooldown(userId));
    sync();
    const onStorage = (event: StorageEvent) => {
      if (event.key === feedbackStorageKey(userId)) sync();
    };
    window.addEventListener(FEEDBACK_COOLDOWN_EVENT, sync);
    window.addEventListener('storage', onStorage);
    let timer: number | undefined;
    const at = Number(localStorage.getItem(feedbackStorageKey(userId)));
    if (Number.isFinite(at)) {
      const remain = at + FEEDBACK_COOLDOWN_MS - Date.now();
      if (remain > 0) timer = window.setTimeout(sync, remain);
    }
    return () => {
      window.removeEventListener(FEEDBACK_COOLDOWN_EVENT, sync);
      window.removeEventListener('storage', onStorage);
      if (timer) window.clearTimeout(timer);
    };
  }, [userId, onCooldown]);

  return onCooldown;
}

export function FeedbackWidget({
  variant = 'link',
  align = 'start',
  className = '',
  wrapperClassName = '',
  style,
}: FeedbackWidgetProps) {
  const { user } = useAuth();
  const userId = feedbackUserId(user);
  const onCooldown = useFeedbackCooldown(userId);
  const location = useLocation();
  const source = resolveFeedbackSource(location.pathname);
  const panelId = useId();
  const [open, setOpen] = useState(variant === 'nav');
  const [rating, setRating] = useState<FeedbackRatingValue | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [askReview, setAskReview] = useState(false);
  const inFlight = useRef(false);
  const reviewing = useRef(false);
  const pinned = useRef(variant === 'nav');
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open || variant !== 'nav') return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || launcherRef.current?.contains(target)) return;
      pinned.current = false;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, variant]);

  if (!userId || (onCooldown && !reviewing.current)) return null;

  const closePanel = () => {
    pinned.current = false;
    setOpen(false);
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (rating == null || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError('');
    try {
      await submitProductFeedback({
        rating,
        message,
        sourcePage: source.sourcePage,
      });
      toast.success('Thank you for your feedback!');
      if (rating === 5) {
        reviewing.current = true;
        pinned.current = true;
        setAskReview(true);
        setOpen(true);
      }
      markFeedbackSubmitted(userId);
    } catch {
      setError(ERROR_MESSAGE);
      toast.error(ERROR_MESSAGE);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const survey = (
    <section
      id={panelId}
      ref={panelRef}
      className="hv-feedback-survey hv-feedback-survey--inline"
      aria-label="Feedback"
    >
      {askReview ? (
        <TrustpilotNote />
      ) : rating == null ? (
        <>
          <h2 className="hv-feedback-title">How satisfied are you with {source.productName}?</h2>
          <div className="hv-feedback-scale" role="radiogroup" aria-label="Satisfaction rating">
            {FEEDBACK_RATINGS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="hv-feedback-face"
                aria-label={option.label}
                onClick={() => setRating(option.value)}
              >
                <span className="hv-feedback-emoji" aria-hidden="true">
                  {option.emoji}
                </span>
              </button>
            ))}
          </div>
          <div className="hv-feedback-scale-labels">
            <span>Very dissatisfied</span>
            <span>Very satisfied</span>
          </div>
        </>
      ) : (
        <form onSubmit={onSubmit}>
          <textarea
            className="hv-feedback-input"
            rows={3}
            value={message}
            disabled={submitting}
            maxLength={FEEDBACK_MESSAGE_MAX}
            aria-label="Feedback message"
            aria-describedby={`${panelId}-count`}
            placeholder="Tell us more about your experience (optional)"
            onChange={(event) => setMessage(event.target.value.slice(0, FEEDBACK_MESSAGE_MAX))}
          />
          <p id={`${panelId}-count`} className="hv-feedback-count">
            {message.length}/{FEEDBACK_MESSAGE_MAX}
          </p>
          {error && (
            <p className="hv-feedback-status hv-feedback-status--error" role="alert">
              {error}
            </p>
          )}
          <div className="hv-feedback-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </div>
        </form>
      )}
    </section>
  );

  const rootClass = [
    'hv-feedback',
    align === 'end' ? 'hv-feedback--end' : '',
    variant === 'nav' ? 'hv-feedback--nav' : '',
    variant === 'nav' && open ? 'hv-feedback--open' : '',
    wrapperClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={rootClass}
        onMouseEnter={() => {
          if (variant !== 'nav') setOpen(true);
        }}
        onMouseLeave={() => {
          if (variant !== 'nav' && !pinned.current) setOpen(false);
        }}
      >
        {variant !== 'nav' && open && (
          <section id={panelId} className="hv-feedback-panel" aria-label="Feedback">
            <div className="hv-feedback-card">
              {askReview ? (
                <TrustpilotNote />
              ) : rating == null ? (
                <div>
                  <h2 className="hv-feedback-title">How satisfied are you with {source.productName}?</h2>
                  <div className="hv-feedback-faces" role="radiogroup" aria-label="Satisfaction rating">
                    {FEEDBACK_RATINGS.map((option) => (
                      <Tooltip key={option.value}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="hv-feedback-face"
                            aria-label={option.label}
                            onClick={() => setRating(option.value)}
                          >
                            <span className="hv-feedback-emoji" aria-hidden="true">
                              {option.emoji}
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{option.label}</TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit}>
                  <button
                    type="button"
                    className="hv-feedback-back"
                    onClick={() => setRating(null)}
                    disabled={submitting}
                  >
                    Back
                  </button>
                  <textarea
                    className="hv-feedback-input"
                    rows={2}
                    value={message}
                    disabled={submitting}
                    maxLength={FEEDBACK_MESSAGE_MAX}
                    aria-label="Feedback message"
                    aria-describedby={`${panelId}-count`}
                    placeholder="Tell us more about your experience (optional)"
                    onChange={(event) => setMessage(event.target.value.slice(0, FEEDBACK_MESSAGE_MAX))}
                  />
                  <p id={`${panelId}-count`} className="hv-feedback-count">
                    {message.length}/{FEEDBACK_MESSAGE_MAX}
                  </p>
                  {error && (
                    <p className="hv-feedback-status hv-feedback-status--error" role="alert">
                      {error}
                    </p>
                  )}
                  <div className="hv-feedback-actions">
                    <Button type="submit" disabled={submitting}>
                      {submitting ? (
                        <>
                          <Spinner size="sm" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Feedback'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </section>
        )}
        {variant === 'nav' ? (
          <>
            <div className="hv-feedback-nav-head">
              <button
                ref={launcherRef}
                type="button"
                className={['hv-feedback-launcher', 'hv-feedback-launcher--nav', className].filter(Boolean).join(' ')}
                aria-expanded={open}
                aria-controls={panelId}
                style={style}
                onClick={() => {
                  setOpen((current) => {
                    if (current && pinned.current) {
                      pinned.current = false;
                      return false;
                    }
                    pinned.current = true;
                    return true;
                  });
                }}
              >
                <MessageSquare size={14} className="shrink-0" strokeWidth={2} aria-hidden="true" />
                <span>Feedback</span>
              </button>
              {open && (
                <button type="button" className="hv-feedback-close" aria-label="Close feedback" onClick={closePanel}>
                  <X size={14} />
                </button>
              )}
            </div>
            {open && survey}
          </>
        ) : (
          <button
            ref={launcherRef}
            type="button"
            className={['hv-feedback-launcher', 'hv-feedback-launcher--link', className].filter(Boolean).join(' ')}
            aria-expanded={open}
            aria-controls={panelId}
            style={style}
            onClick={() => {
              setOpen((current) => {
                if (current && pinned.current) {
                  pinned.current = false;
                  return false;
                }
                pinned.current = true;
                return true;
              });
            }}
          >
            <span>Feedback</span>
          </button>
        )}
      </div>
    </TooltipProvider>
  );
}

export default FeedbackWidget;
